
import React from 'react';
import { HelpCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLocation, useNavigate } from 'react-router-dom';
import StepIndicator from './StepIndicator';
import { useAuth } from '../contexts/AuthContext';
import shomriLogo from '@/assets/shomri-logo.svg';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isLoginPage = location.pathname === '/login';
  const { logout } = useAuth();
  
  // Consider the second page after login to be the child selection or device detection page
  const isSecondPage = location.pathname === '/' || location.pathname === '/device-detection';
  
  // Hide back button only on install-profile page
  const hideBackButton = location.pathname === '/install-profile';
  
  // Show start over button on confirmation page
  const isConfirmationPage = location.pathname === '/confirmation';
  
  const handleBackOrStartOver = () => {
    if (isSecondPage || isConfirmationPage) {
      logout(); // Start over on second page or confirmation page
    } else {
      navigate(-1); // Go back to previous page
    }
  };

  const getButtonText = () => {
    if (isSecondPage) return 'Start Over';
    if (isConfirmationPage) return 'Start Over';
    return 'Back';
  };

  return (
    <div className="w-full h-full bg-background overflow-hidden flex flex-col">

      {/* Header with Shomri branding */}
      <header className="bg-card shadow-sm border-b border-border flex-shrink-0">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            {!isLoginPage && !hideBackButton && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleBackOrStartOver}
                className="flex items-center space-x-2 text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>{getButtonText()}</span>
              </Button>
            )}
            
            {/* Shomri Logo - centered */}
            {!isLoginPage && (
              <div className="flex-1 flex justify-center">
                <img src={shomriLogo} alt="Shomri" className="h-6" />
              </div>
            )}
            
            {/* Need help button on the right */}
            {!isLoginPage && (
              <Button 
                variant="ghost" 
                size="sm"
                className="flex items-center space-x-2 text-muted-foreground hover:text-foreground"
              >
                <HelpCircle className="w-4 h-4" />
                <span>Need help?</span>
              </Button>
            )}
          </div>
          
          {/* Step Indicator below */}
          {!isLoginPage && (
            <div className="flex justify-center mt-3">
              <StepIndicator />
            </div>
          )}
        </div>
      </header>

      {/* Main Content - Flexible height with scroll if needed */}
      <main className="flex-1 px-4 py-3 overflow-hidden">
        <div className="bg-card rounded-xl shadow-lg border border-border h-full overflow-y-auto">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
